/** Encode/decode compact replay frame streams (browser + Node). */

function bytesToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes).toString("base64");
  }
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s);
}

function base64ToBytes(b64: string): Uint8Array {
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(b64, "base64"));
  }
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Clamp dt seconds to 1..50 ms for storage. */
export function quantizeDtMs(dtSec: number): number {
  if (!(dtSec > 0) || !Number.isFinite(dtSec)) return 16;
  return Math.max(1, Math.min(50, Math.round(dtSec * 1000)));
}

export function encodeDts(dtMsList: number[]): string {
  const buf = new Uint8Array(dtMsList.length);
  for (let i = 0; i < dtMsList.length; i++) {
    buf[i] = Math.max(1, Math.min(50, Math.floor(dtMsList[i]!) || 16));
  }
  return bytesToBase64(buf);
}

export function decodeDts(b64: string): number[] {
  const buf = base64ToBytes(b64);
  const out: number[] = new Array(buf.length);
  for (let i = 0; i < buf.length; i++) out[i] = buf[i] || 16;
  return out;
}

/** Encode normalized pointer samples (0–1) as Uint16 LE pairs. */
export function encodePointers(samples: Array<{ x: number; y: number }>): string {
  const buf = new Uint8Array(samples.length * 4);
  const view = new DataView(buf.buffer);
  for (let i = 0; i < samples.length; i++) {
    const nx = Math.max(0, Math.min(1, samples[i]!.x));
    const ny = Math.max(0, Math.min(1, samples[i]!.y));
    view.setUint16(i * 4, Math.round(nx * 65535), true);
    view.setUint16(i * 4 + 2, Math.round(ny * 65535), true);
  }
  return bytesToBase64(buf);
}

export function decodePointers(b64: string): Array<{ x: number; y: number }> {
  const buf = base64ToBytes(b64);
  if (buf.length % 4 !== 0) return [];
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const n = buf.length / 4;
  const out: Array<{ x: number; y: number }> = new Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = {
      x: view.getUint16(i * 4, true) / 65535,
      y: view.getUint16(i * 4 + 2, true) / 65535,
    };
  }
  return out;
}

export function sumDtMs(dtMsList: number[]): number {
  let s = 0;
  for (const d of dtMsList) s += d;
  return s;
}

/** Reconstruct survival score from frame dts (score ≈ 60 pts/sec). */
export function scoreFromDts(dtMsList: number[], scorePerSec = 60): number {
  let score = 0;
  for (const ms of dtMsList) {
    score += scorePerSec * (ms / 1000);
  }
  return Math.floor(score);
}
